import { useState, useEffect, useCallback } from "react";
import { FiSearch, FiArrowLeft, FiChevronRight, FiCheck, FiX } from "react-icons/fi";
import { apiGet, apiPost, apiPut, apiDelete } from "../api";
import { PageHeader, Card, StatCard, Pill, Btn, THead, TD, Icon, fmtInvNo, Pagination, PER_PAGE_DEFAULT } from "./KioskUI";
import { InvoiceDetail } from "./Invoices";
import BulkImport from "./BulkImport";

const CUSTOMER_IMPORT_COLUMNS = [
  { key: "name", label: "Name", required: true },
  { key: "contact", label: "Contact" },
  { key: "phone", label: "Phone" },
  { key: "town", label: "Town" },
];

const MMK = (n) => Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 });
const fmtDate = (s) => new Date(s).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
const numOnly = (s) => (String(s).match(/[\d.]/g) || []).join("");
const METHOD_LABEL = { CASH: "Cash", BANK_TRANSFER: "Bank transfer", CHECK: "Check", OTHER: "Other" };

/* ─── Record Payment modal (single customer, pre-filled) ─── */
function RecordPaymentModal({ customer, token, onClose, onSaved }) {
  const [amount, setAmount]     = useState("");
  const [method, setMethod]     = useState("CASH");
  const [note, setNote]         = useState("");
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");

  const balance = Number(customer.balance);
  const canSave = amount && Number(amount) > 0 && !saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true); setError("");
    try {
      const res = await apiPost("/api/inventory/payments", {
        customer: customer.id,
        amount: parseFloat(amount),
        payment_method: method,
        note: note.trim(),
      }, token, "employee-token");
      if (res.ok) { onSaved(await res.json()); }
      else { const err = await res.json().catch(() => ({})); setError(err.error || "Failed to record payment."); }
    } catch { setError("Cannot connect to server."); }
    finally { setSaving(false); }
  };

  const lbl = { display: "block", fontSize: 11.5, fontWeight: 700, color: "var(--sub)", letterSpacing: ".02em", marginBottom: 6 };
  const field = { display: "flex", alignItems: "center", border: "1px solid var(--line)", borderRadius: "var(--r-sm)", padding: "10px 13px", background: "var(--surface)" };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(19,24,32,.42)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }}>
      <form onClick={e => e.stopPropagation()} onSubmit={handleSubmit}
        style={{ background: "var(--surface)", borderRadius: "var(--r-xl)", boxShadow: "0 24px 70px rgba(19,24,32,.3)", width: 420, maxWidth: "94vw", padding: "24px 26px 22px", display: "flex", flexDirection: "column" }}>

        {/* header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14, marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 13 }}>
            <div style={{ width: 44, height: 44, borderRadius: 13, background: "var(--success-bg)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon name="payments" size={21} stroke="var(--success)" />
            </div>
            <div>
              <div style={{ fontFamily: "var(--font-head)", fontSize: 20, fontWeight: 800, color: "var(--ink)", letterSpacing: "-.02em" }}>
                Record Payment
              </div>
              <div style={{ fontSize: 13, color: "var(--sub)", marginTop: 3 }}>
                {customer.name} · <span style={{ color: "var(--warning)", fontWeight: 600 }}>{MMK(balance)} Ks</span> outstanding
              </div>
            </div>
          </div>
          <button type="button" onClick={onClose} style={{ border: "1px solid var(--line)", background: "var(--surface)", borderRadius: "var(--r-sm)", padding: 7, cursor: "pointer", display: "flex", flexShrink: 0 }}>
            <FiX size={16} color="var(--sub)" />
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* amount */}
          <div>
            <label style={lbl}>Amount received</label>
            <div style={{ ...field, gap: 8 }}>
              <input autoFocus type="number" value={amount} onChange={e => setAmount(e.target.value)}
                placeholder="0" min="1" max={balance || undefined} required
                style={{ border: "none", outline: "none", background: "transparent", width: "100%", fontFamily: "var(--font-body)", fontSize: 15, fontWeight: 700, color: "var(--ink)", textAlign: "right", fontVariantNumeric: "tabular-nums" }} />
              <span style={{ fontSize: 13, color: "var(--n-400)", fontWeight: 600, whiteSpace: "nowrap" }}>Ks</span>
            </div>
            {amount && Number(amount) > 0 && balance > 0 && (
              <div style={{ marginTop: 6, fontSize: 12.5, color: "var(--sub)" }}>
                Balance after: <span style={{ fontWeight: 700, color: (balance - Number(amount)) <= 0 ? "var(--success)" : "var(--warning)" }}>
                  {MMK(Math.max(0, balance - Number(amount)))} Ks
                </span>
                {(balance - Number(amount)) <= 0 && <span style={{ color: "var(--success)", marginLeft: 6 }}>· Settled ✓</span>}
              </div>
            )}
          </div>

          {/* payment method */}
          <div>
            <label style={lbl}>Method</label>
            <div style={{ display: "flex", gap: 8 }}>
              {[["CASH","Cash"],["BANK_TRANSFER","Bank"],["OTHER","Other"]].map(([m, t]) => (
                <button key={m} type="button" onClick={() => setMethod(m)}
                  style={{ flex: 1, padding: "9px", borderRadius: "var(--r-sm)", cursor: "pointer", border: `1px solid ${method === m ? "var(--primary)" : "var(--line)"}`, background: method === m ? "var(--blue-50)" : "var(--surface)", color: method === m ? "var(--primary)" : "var(--n-600)", fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 13.5 }}>
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* note */}
          <div>
            <label style={lbl}>Note <span style={{ fontWeight: 500, color: "var(--n-400)" }}>optional</span></label>
            <div style={field}>
              <input value={note} onChange={e => setNote(e.target.value)} placeholder="Bank slip, reference number…"
                style={{ border: "none", outline: "none", background: "transparent", width: "100%", fontFamily: "var(--font-body)", fontSize: 14.5, color: "var(--ink)" }} />
            </div>
          </div>
        </div>

        {error && (
          <div style={{ marginTop: 12, fontSize: 13, color: "var(--danger)", padding: "8px 12px", background: "var(--danger-bg)", borderRadius: "var(--r-sm)" }}>{error}</div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <Btn kind="ghost" onClick={onClose} style={{ flex: 1, justifyContent: "center" }}>Cancel</Btn>
          <button type="submit" disabled={!canSave}
            style={{ flex: 1, justifyContent: "center", display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 14.5, borderRadius: "var(--r-sm)", padding: "11px 18px", border: "none", cursor: canSave ? "pointer" : "not-allowed", background: canSave ? "var(--primary)" : "var(--n-200)", color: "#fff" }}>
            <FiCheck size={16} /> {saving ? "Saving…" : "Record Payment"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ─── Add / Edit modal ─── */
function CustomerModal({ existing, token, onClose, onSaved }) {
  const isEdit = !!existing;
  const [form, setForm] = useState({
    name: existing?.name || "",
    contact: existing?.contact || "",
    phone: existing?.phone || "",
    town: existing?.town || "",
    balance: isEdit ? "" : "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const canSave = form.name.trim().length > 0 && !saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true); setError("");
    try {
      const payload = {
        name: form.name.trim(),
        contact: form.contact.trim(),
        phone: form.phone.trim(),
        town: form.town.trim(),
        ...((!isEdit && form.balance) ? { balance: parseFloat(form.balance) || 0 } : {}),
      };
      const res = isEdit
        ? await apiPut(`/api/inventory/customers/${existing.id}`, payload, token)
        : await apiPost("/api/inventory/customers", payload, token);
      if (res.ok) {
        onSaved(await res.json(), !isEdit);
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.error || err.detail || "Failed to save.");
      }
    } catch { setError("Cannot connect to server."); }
    finally { setSaving(false); }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(19,24,32,.42)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }}>
      <form onClick={e => e.stopPropagation()} onSubmit={handleSubmit}
        style={{ background: "var(--surface)", borderRadius: "var(--r-xl)", boxShadow: "0 24px 70px rgba(19,24,32,.3)", width: 480, maxWidth: "94vw", padding: "24px 26px 22px", display: "flex", flexDirection: "column" }}>

        {/* header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14, marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 13 }}>
            <div style={{ width: 44, height: 44, borderRadius: 13, background: "var(--blue-50)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon name="customers" size={21} stroke="var(--primary)" />
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 9 }}>
                <span style={{ fontFamily: "var(--font-head)", fontSize: 20, fontWeight: 800, color: "var(--ink)", letterSpacing: "-.02em" }}>
                  {isEdit ? "Edit Customer" : "Add Customer"}
                </span>
                <span style={{ fontFamily: "var(--font-mm)", fontSize: 14, color: "var(--n-400)" }}>ဖောက်သည်</span>
              </div>
              <div style={{ fontSize: 13, color: "var(--sub)", marginTop: 3 }}>A wholesale buyer you sell to.</div>
            </div>
          </div>
          <button type="button" onClick={onClose} style={{ border: "1px solid var(--line)", background: "var(--surface)", borderRadius: "var(--r-sm)", padding: 7, cursor: "pointer", display: "flex", flexShrink: 0 }}>
            <FiX size={16} color="var(--sub)" />
          </button>
        </div>

        {/* fields */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <MField label="Customer name" mm="အမည်" required style={{ gridColumn: "1 / -1" }}>
            <input autoFocus value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Yangon Bazaar" style={inpSt} />
          </MField>
          <MField label="Contact person">
            <input value={form.contact} onChange={e => set("contact", e.target.value)} placeholder="e.g. Daw Hla" style={inpSt} />
          </MField>
          <MField label="Phone">
            <input value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="09 …" style={inpSt} />
          </MField>
          <MField label="Town">
            <input value={form.town} onChange={e => set("town", e.target.value)} placeholder="e.g. Yangon" style={inpSt} />
          </MField>
          {!isEdit && (
            <MField label="Opening balance" hint="carry over from books" unit="Ks">
              <input value={form.balance} onChange={e => set("balance", numOnly(e.target.value))} placeholder="0" style={{ ...inpSt, textAlign: "right", fontVariantNumeric: "tabular-nums" }} />
            </MField>
          )}
        </div>

        {error && (
          <div style={{ marginTop: 12, fontSize: 13, color: "var(--danger)", padding: "8px 12px", background: "var(--danger-bg)", borderRadius: "var(--r-sm)" }}>{error}</div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <Btn kind="ghost" onClick={onClose} style={{ flex: 1, justifyContent: "center" }}>Cancel</Btn>
          <button type="submit" disabled={!canSave}
            style={{ flex: 1, justifyContent: "center", display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 14.5, borderRadius: "var(--r-sm)", padding: "11px 18px", border: "none", cursor: canSave ? "pointer" : "not-allowed", background: canSave ? "var(--primary)" : "var(--n-200)", color: "#fff" }}>
            <FiCheck size={16} /> {saving ? "Saving…" : isEdit ? "Save Changes" : "Add Customer"}
          </button>
        </div>
      </form>
    </div>
  );
}

function MField({ label, mm, hint, required, unit, style, children }) {
  return (
    <div style={style}>
      <label style={{ display: "flex", gap: 7, alignItems: "baseline", fontSize: 12.5, fontWeight: 700, color: "var(--sub)", letterSpacing: ".02em", marginBottom: 7 }}>
        {label}
        {required && <span style={{ color: "var(--danger)" }}>·</span>}
        {mm && <span style={{ fontFamily: "var(--font-mm)", fontWeight: 500, color: "var(--n-400)" }}>{mm}</span>}
        {hint && <span style={{ fontWeight: 500, color: "var(--n-400)" }}>{hint}</span>}
      </label>
      <div style={{ display: "flex", alignItems: "center", border: "1px solid var(--line)", borderRadius: "var(--r-sm)", padding: "10px 13px", background: "var(--surface)", gap: 8 }}>
        {children}
        {unit && <span style={{ fontSize: 13, color: "var(--n-400)", fontWeight: 600, whiteSpace: "nowrap" }}>{unit}</span>}
      </div>
    </div>
  );
}
const inpSt = { border: "none", outline: "none", background: "transparent", width: "100%", fontFamily: "var(--font-body)", fontSize: 14.5, color: "var(--ink)" };

/* ─── Delete confirm ─── */
function DeleteConfirm({ customer, onCancel, onConfirm, busy }) {
  const hasBalance = Number(customer.balance) > 0;
  return (
    <div onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(19,24,32,.42)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "var(--surface)", borderRadius: "var(--r-xl)", boxShadow: "0 24px 70px rgba(19,24,32,.3)", width: 380, padding: "26px 26px 22px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 13, marginBottom: 14 }}>
          <div style={{ width: 42, height: 42, borderRadius: 12, background: "var(--danger-bg)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Icon name="trash" size={20} stroke="var(--danger)" />
          </div>
          <div>
            <div style={{ fontFamily: "var(--font-head)", fontSize: 17, fontWeight: 800, color: "var(--ink)" }}>Delete "{customer.name}"?</div>
            <div style={{ fontSize: 13.5, color: "var(--sub)", marginTop: 4, lineHeight: 1.5 }}>
              {hasBalance
                ? `They still have ${MMK(customer.balance)} Ks outstanding — settle it first.`
                : "Their past invoices are kept for records."}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
          <Btn kind="ghost" onClick={onCancel} style={{ flex: 1, justifyContent: "center" }}>Cancel</Btn>
          <button onClick={onConfirm} disabled={busy || hasBalance}
            style={{ flex: 1, justifyContent: "center", display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 14.5, borderRadius: "var(--r-sm)", padding: "11px 18px", border: "none", cursor: (busy || hasBalance) ? "not-allowed" : "pointer", background: (busy || hasBalance) ? "var(--n-200)" : "var(--danger)", color: "#fff" }}>
            <Icon name="trash" size={16} stroke="#fff" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Customer detail ─── */
function CustomerDetail({ customerId, token, go, onBack, onSaved, onDeleted }) {
  const [customer, setCustomer]   = useState(null);
  const [sales, setSales]         = useState([]);
  const [form, setForm]           = useState(null);
  const [saving, setSaving]       = useState(false);
  const [delConfirm, setDelConfirm] = useState(false);
  const [error, setError]         = useState("");
  const [loadingSales, setLoadingSales]   = useState(true);
  const [payments, setPayments]           = useState([]);
  const [invoiceId, setInvoiceId]         = useState(null);
  const [recordingPayment, setRecordingPayment] = useState(false);

  const fetchDetail = useCallback(async () => {
    try {
      const [c, sd, pd] = await Promise.all([
        apiGet(`/api/inventory/customers/${customerId}`, token).then(r => r.json()),
        apiGet(`/api/inventory/customers/${customerId}/sales`, token).then(r => r.json()),
        apiGet(`/api/inventory/payments?customer_id=${customerId}`, token).then(r => r.json()),
      ]);
      setCustomer(c);
      setForm({ name: c.name, contact: c.contact || "", phone: c.phone || "", town: c.town || "" });
      setSales(Array.isArray(sd.sales) ? sd.sales : []);
      setPayments(Array.isArray(pd) ? pd : []);
    } catch (err) { console.error(err); }
    finally { setLoadingSales(false); }
  }, [customerId, token]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const handleSave = async () => {
    setSaving(true); setError("");
    try {
      const res = await apiPut(`/api/inventory/customers/${customerId}`, form, token);
      if (res.ok) {
        const updated = await res.json();
        setCustomer(updated);
        onSaved(updated);
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.error || "Failed to save.");
      }
    } catch { setError("Cannot connect to server."); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await apiDelete(`/api/inventory/customers/${customerId}`, token);
      if (res.ok) { onDeleted(customerId); }
      else { const err = await res.json().catch(() => ({})); setError(err.error || "Failed to delete."); setDelConfirm(false); }
    } catch { setError("Cannot connect to server."); setDelConfirm(false); }
    finally { setSaving(false); }
  };

  if (!customer || !form) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 240 }}>
      <span style={{ color: "var(--sub)", fontSize: 14 }}>Loading…</span>
    </div>
  );

  if (invoiceId) {
    return (
      <InvoiceDetail
        saleId={invoiceId}
        token={token}
        go={go}
        onBack={() => setInvoiceId(null)}
      />
    );
  }

  const balance = Number(customer.balance);
  const totalInvoiced = sales.reduce((s, i) => s + Number(i.total_amount || 0), 0);
  const firstName = customer.name.split(" ")[0];

  return (
    <div>
      {/* header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
        <button onClick={onBack} style={{ border: "1px solid var(--line)", background: "var(--surface)", borderRadius: "var(--r-sm)", padding: 8, cursor: "pointer", display: "flex" }}>
          <FiArrowLeft size={18} color="var(--sub)" />
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontFamily: "var(--font-head)", fontSize: 25, fontWeight: 800, letterSpacing: "-.025em", margin: 0, color: "var(--ink)" }}>{customer.name}</h1>
          <div style={{ fontSize: 13.5, color: "var(--sub)", marginTop: 3 }}>
            #{customer.id}{customer.town ? ` · ${customer.town}` : ""}{customer.contact ? ` · ${customer.contact}` : ""}
          </div>
        </div>
        {balance > 0 && <Pill tone="low">{MMK(balance)} Ks balance</Pill>}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.55fr 1fr", gap: 16, alignItems: "start" }}>
        {/* left */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* editable details */}
          <Card style={{ padding: 24 }}>
            <div style={{ fontFamily: "var(--font-head)", fontSize: 15, fontWeight: 700, color: "var(--ink)", marginBottom: 18 }}>Customer details</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <DetailField label="Business name" mm="အမည်" style={{ gridColumn: "1 / -1" }}>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inpSt} />
              </DetailField>
              <DetailField label="Contact person">
                <input value={form.contact} onChange={e => setForm(f => ({ ...f, contact: e.target.value }))} placeholder="—" style={inpSt} />
              </DetailField>
              <DetailField label="Phone">
                <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="—" style={inpSt} />
              </DetailField>
              <DetailField label="Town" style={{ gridColumn: "1 / -1" }}>
                <input value={form.town} onChange={e => setForm(f => ({ ...f, town: e.target.value }))} placeholder="—" style={inpSt} />
              </DetailField>
            </div>
            {error && <div style={{ marginTop: 12, fontSize: 13, color: "var(--danger)", padding: "8px 12px", background: "var(--danger-bg)", borderRadius: "var(--r-sm)" }}>{error}</div>}
          </Card>

          {/* sales table */}
          <Card style={{ overflow: "hidden" }}>
            <div style={{ padding: "15px 20px", borderBottom: "1px solid var(--hair)", display: "flex", alignItems: "baseline", gap: 10 }}>
              <div style={{ fontFamily: "var(--font-head)", fontSize: 16, fontWeight: 700, color: "var(--ink)" }}>Sales</div>
              <span style={{ fontFamily: "var(--font-mm)", fontSize: 13.5, color: "var(--n-400)" }}>အရောင်းမှတ်တမ်း</span>
              <span style={{ marginLeft: "auto", fontSize: 12.5, color: "var(--sub)" }}>{sales.length} invoices</span>
            </div>
            {loadingSales ? (
              <div style={{ padding: "28px 20px", textAlign: "center", color: "var(--sub)", fontSize: 13.5 }}>Loading…</div>
            ) : sales.length === 0 ? (
              <div style={{ padding: "30px 20px", textAlign: "center", color: "var(--n-400)", fontSize: 13.5 }}>No sales recorded for this customer yet.</div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <THead cols={[{ t: "Invoice" }, { t: "Date" }, { t: "Items", r: 1 }, { t: "Type" }, { t: "Status" }, { t: "Total", r: 1 }, { t: "", r: 1, w: 40 }]} />
                <tbody>
                  {sales.map(s => {
                    const isCredit = s.payment_type === "Credit";
                    return (
                    <tr key={s.sale_id}
                      style={{ cursor: "pointer" }}
                      onClick={() => setInvoiceId(s.sale_id)}
                      onMouseEnter={e => e.currentTarget.style.background = "var(--faint)"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >
                      <TD b>{fmtInvNo(s.sale_id)}</TD>
                      <TD mut>{fmtDate(s.date)}</TD>
                      <TD r mut>{s.items_count}</TD>
                      <td style={{ padding: "14px 16px", borderTop: "1px solid var(--hair)" }}>
                        <Pill tone={isCredit ? "credit" : "cash"}>{s.payment_type}</Pill>
                      </td>
                      <td style={{ padding: "14px 16px", borderTop: "1px solid var(--hair)" }}>
                        <Pill tone={isCredit ? "low" : "ok"}>{isCredit ? "Open" : "Paid"}</Pill>
                      </td>
                      <TD r b>{MMK(s.total_amount)}</TD>
                      <td style={{ padding: "14px 16px", borderTop: "1px solid var(--hair)", textAlign: "right" }}>
                        <FiChevronRight size={17} color="var(--n-300)" />
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Card>

          {/* payments received */}
          <Card style={{ overflow: "hidden" }}>
            <div style={{ padding: "15px 20px", borderBottom: "1px solid var(--hair)", display: "flex", alignItems: "baseline", gap: 10 }}>
              <div style={{ fontFamily: "var(--font-head)", fontSize: 16, fontWeight: 700, color: "var(--ink)" }}>Payments</div>
              <span style={{ fontFamily: "var(--font-mm)", fontSize: 13.5, color: "var(--n-400)" }}>ငွေပေးချေမှု</span>
              <span style={{ marginLeft: "auto", fontSize: 12.5, color: "var(--sub)" }}>{payments.length} received</span>
            </div>
            {loadingSales ? (
              <div style={{ padding: "28px 20px", textAlign: "center", color: "var(--sub)", fontSize: 13.5 }}>Loading…</div>
            ) : payments.length === 0 ? (
              <div style={{ padding: "30px 20px", textAlign: "center", color: "var(--n-400)", fontSize: 13.5 }}>
                No payments received from this customer yet.
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <THead cols={[{ t: "Date" }, { t: "Method" }, { t: "Note" }, { t: "Amount", r: 1 }]} />
                <tbody>
                  {payments.map(p => (
                    <tr key={p.id}
                      onMouseEnter={e => e.currentTarget.style.background = "var(--faint)"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >
                      <TD mut>{fmtDate(p.created_at)}</TD>
                      <TD mut>{METHOD_LABEL[p.payment_method] || p.payment_method}</TD>
                      <TD mut>{p.note || "—"}</TD>
                      <TD r b style={{ color: "var(--success)" }}>+{MMK(p.amount)}</TD>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>

        {/* right */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* account summary */}
          <Card style={{ padding: 24 }}>
            <div style={{ fontFamily: "var(--font-head)", fontSize: 15, fontWeight: 700, color: "var(--ink)", marginBottom: 6 }}>Account summary</div>
            <div style={{ fontSize: 12.5, color: "var(--sub)", marginBottom: 14 }}>{sales.length} invoice{sales.length !== 1 ? "s" : ""} on record</div>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12 }}>
              <span style={{ fontSize: 13.5, color: "var(--sub)" }}>Total invoiced</span>
              <span style={{ fontFamily: "var(--font-head)", fontSize: 22, fontWeight: 800, color: "var(--ink)", fontVariantNumeric: "tabular-nums", letterSpacing: "-.02em" }}>
                {MMK(totalInvoiced)} <span style={{ fontSize: 11, color: "var(--sub)" }}>Ks</span>
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 0", borderTop: "1px solid var(--hair)" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 13.5, color: "var(--sub)" }}>
                <span style={{ width: 8, height: 8, borderRadius: 3, background: "var(--primary)" }} /> Invoices
              </span>
              <span style={{ fontFamily: "var(--font-head)", fontSize: 14, fontWeight: 700, color: "var(--ink)" }}>{sales.length}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 12, marginTop: 4 }}>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: "var(--ink)" }}>Outstanding balance</span>
              <span style={{ fontFamily: "var(--font-head)", fontSize: 17, fontWeight: 800, color: balance > 0 ? "var(--warning)" : "var(--n-400)", fontVariantNumeric: "tabular-nums" }}>
                {balance > 0 ? MMK(balance) + " Ks" : "Settled"}
              </span>
            </div>
          </Card>

          {/* actions */}
          <div style={{ display: "flex", gap: 11 }}>
            <Btn kind="ghost" onClick={onBack} style={{ flex: 1, justifyContent: "center" }}>Cancel</Btn>
            <Btn kind="pri" icon="check" onClick={handleSave} disabled={saving} style={{ flex: 1, justifyContent: "center" }}>
              {saving ? "Saving…" : "Save"}
            </Btn>
          </div>
          {balance > 0 && (
            <Btn kind="pri" icon="payments" onClick={() => setRecordingPayment(true)} style={{ justifyContent: "center" }}>
              Record payment
            </Btn>
          )}
          <Btn kind="acc" icon="sale" onClick={() => go("sale")} style={{ justifyContent: "center" }}>
            New sale for {firstName}
          </Btn>
          <Btn kind="danger" icon="trash" onClick={() => setDelConfirm(true)} style={{ justifyContent: "center" }}>
            Delete customer
          </Btn>
        </div>
      </div>

      {delConfirm && (
        <DeleteConfirm customer={customer} busy={saving} onCancel={() => setDelConfirm(false)} onConfirm={handleDelete} />
      )}

      {recordingPayment && (
        <RecordPaymentModal
          customer={customer}
          token={token}
          onClose={() => setRecordingPayment(false)}
          onSaved={(payment) => {
            setRecordingPayment(false);
            fetchDetail(); // re-fetch customer + payments list
            const newBalance = Math.max(0, Number(customer.balance) - Number(payment.amount));
            onSaved({ ...customer, balance: newBalance });
          }}
        />
      )}
    </div>
  );
}

function DetailField({ label, mm, style, children }) {
  return (
    <div style={style}>
      <label style={{ fontSize: 12.5, fontWeight: 700, color: "var(--sub)", letterSpacing: ".02em", display: "flex", gap: 7, alignItems: "baseline", marginBottom: 7 }}>
        {label}{mm && <span style={{ fontFamily: "var(--font-mm)", fontWeight: 500, color: "var(--n-400)" }}>{mm}</span>}
      </label>
      <div style={{ display: "flex", alignItems: "center", border: "1px solid var(--line)", borderRadius: "var(--r-sm)", padding: "11px 13px", background: "var(--surface)" }}>
        {children}
      </div>
    </div>
  );
}

/* ─── Customer list ─── */
export default function Customers({ token, go }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [q, setQ]                 = useState("");
  const [page, setPage]           = useState(1);
  const [adding, setAdding]       = useState(false);
  const [importing, setImporting] = useState(false);
  const [view, setView]           = useState("list");
  const [selectedId, setSelectedId] = useState(null);

  const fetchCustomers = useCallback(async () => {
    try {
      const r = await apiGet("/api/inventory/customers", token);
      const d = await r.json();
      setCustomers(Array.isArray(d) ? d : []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const openDetail = (id) => { setSelectedId(id); setView("detail"); };
  const backToList = () => { setView("list"); setSelectedId(null); };

  const handleAdded = (c) => { setCustomers(prev => [c, ...prev]); setAdding(false); openDetail(c.id); };
  const handleSaved = (c) => setCustomers(prev => prev.map(x => x.id === c.id ? c : x));
  const handleDeleted = (id) => { setCustomers(prev => prev.filter(x => x.id !== id)); backToList(); };

  if (view === "detail") {
    return (
      <CustomerDetail
        customerId={selectedId}
        token={token}
        go={go}
        onBack={backToList}
        onSaved={handleSaved}
        onDeleted={handleDeleted}
      />
    );
  }

  const owed  = customers.reduce((s, c) => s + Number(c.balance), 0);
  const withBalance = customers.filter(c => Number(c.balance) > 0).length;
  const qq    = q.trim().toLowerCase();
  const filtered = customers.filter(c =>
    !qq || c.name.toLowerCase().includes(qq) ||
    (c.contact || "").toLowerCase().includes(qq) ||
    (c.phone || "").replace(/\s/g, "").includes(qq.replace(/\s/g, "")) ||
    (c.town || "").toLowerCase().includes(qq)
  );
  const list = filtered.slice((page - 1) * PER_PAGE_DEFAULT, page * PER_PAGE_DEFAULT);

  return (
    <div>
      <PageHeader title="Customers" mm="ဖောက်သည်" desc={`${customers.length} wholesale buyer${customers.length !== 1 ? "s" : ""}`}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--r-sm)", padding: "9px 13px", width: 280 }}>
          <FiSearch size={16} color="var(--n-400)" style={{ flexShrink: 0 }} />
          <input value={q} onChange={e => { setQ(e.target.value); setPage(1); }} placeholder="Search customers…"
            style={{ border: "none", outline: "none", background: "transparent", fontFamily: "var(--font-body)", fontSize: 14, color: "var(--ink)", width: "100%" }} />
        </div>
        <Btn kind="ghost" onClick={() => setImporting(true)}>Import</Btn>
        <Btn kind="pri" icon="plus" onClick={() => setAdding(true)}>Add Customer</Btn>
      </PageHeader>

      {importing && (
        <BulkImport
          title="Customers"
          columns={CUSTOMER_IMPORT_COLUMNS}
          endpoint="/api/inventory/customers/bulk"
          token={token}
          onClose={() => setImporting(false)}
          onDone={fetchCustomers}
        />
      )}

      {/* stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16, marginBottom: 18 }}>
        <StatCard label="Total Customers" value={customers.length} icon="customers" tone="blue" />
        <StatCard label="With Balance" value={withBalance} icon="payments" tone="amber" sub="have an open balance" />
        <StatCard label="Outstanding" value={(owed / 1e6).toFixed(2) + "M"} unit="MMK" icon="alert" tone="amber" sub={MMK(owed) + " Ks owed"} />
      </div>

      {/* table */}
      <Card style={{ overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: "48px 20px", textAlign: "center", color: "var(--sub)", fontSize: 14 }}>Loading…</div>
        ) : (
          <>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <THead cols={[{ t: "Customer" }, { t: "Contact" }, { t: "Town" }, { t: "Balance", r: 1 }, { t: "", r: 1, w: 40 }]} />
            <tbody>
              {list.length === 0 && (
                <tr><td colSpan={5} style={{ padding: "32px 16px", textAlign: "center", color: "var(--n-400)", fontSize: 14 }}>
                  {qq ? `No customers match "${q}".` : "No customers yet."}
                </td></tr>
              )}
              {list.map(c => {
                const bal = Number(c.balance);
                return (
                  <tr key={c.id} style={{ cursor: "pointer" }} onClick={() => openDetail(c.id)}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--faint)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    <TD b>{c.name}</TD>
                    <TD mut>{[c.contact, c.phone].filter(Boolean).join(" · ") || "—"}</TD>
                    <TD mut>{c.town || "—"}</TD>
                    <TD r b style={{ color: bal > 0 ? "var(--warning)" : "var(--n-400)" }}>
                      {bal > 0 ? MMK(bal) : "—"}
                    </TD>
                    <td style={{ padding: "14px 16px", borderTop: "1px solid var(--hair)", textAlign: "right" }}>
                      <FiChevronRight size={17} color="var(--n-300)" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pagination total={filtered.length} page={page} onChange={setPage} />
          </>
        )}
      </Card>

      {adding && (
        <CustomerModal token={token} onClose={() => setAdding(false)} onSaved={handleAdded} />
      )}
    </div>
  );
}
